import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatPatentDisplayCaption } from "./accountDetailUtils";
import { partyTheme } from "./accountDetailTheme";

function AccountPatentReader({
  loading,
  error,
  patent,
  sourceLabel,
  highlightedClaimNumber,
}) {
  const claims = patent?.claims || [];
  const trimmedPatentName =
    patent?.patent_name != null && String(patent.patent_name).trim() !== ""
      ? String(patent.patent_name).trim()
      : null;
  const publicPatentId =
    patent?.application_number != null &&
    String(patent.application_number).trim() !== ""
      ? String(patent.application_number).trim()
      : null;
  const recordPatentId =
    patent?.patent_id != null && String(patent.patent_id).trim() !== ""
      ? String(patent.patent_id).trim()
      : null;
  const showRecordIdRow =
    Boolean(publicPatentId) &&
    Boolean(recordPatentId) &&
    recordPatentId !== publicPatentId;
  const claimRefs = useRef({});
  const [activeClaimNumber, setActiveClaimNumber] = useState(
    highlightedClaimNumber ? String(highlightedClaimNumber) : null,
  );

  const availableClaimNumbers = useMemo(
    () =>
      new Set(
        claims
          .map((claim) =>
            claim?.claim_number != null ? String(claim.claim_number) : null,
          )
          .filter(Boolean),
      ),
    [claims],
  );

  const parseClaimReferences = (claimText) => {
    const claimRefPattern = /(claim(?:s)?\s+\d+(?:\s*-\s*\d+)?)/gi;
    const parts = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(claimRefPattern);

    while ((match = regex.exec(claimText)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: "text",
          content: claimText.substring(lastIndex, match.index),
        });
      }
      parts.push({
        type: "claim-ref",
        content: match[0],
        claimNumber: match[0].match(/\d+/)?.[0] || null,
      });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < claimText.length) {
      parts.push({
        type: "text",
        content: claimText.substring(lastIndex),
      });
    }

    return parts;
  };

  const jumpToClaim = (claimNumber) => {
    const claimKey = String(claimNumber || "");
    if (!claimKey || !availableClaimNumbers.has(claimKey)) return;
    setActiveClaimNumber(claimKey);
    const node = claimRefs.current[claimKey];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  useEffect(() => {
    if (highlightedClaimNumber != null) {
      jumpToClaim(String(highlightedClaimNumber));
    }
  }, [patent?.patent_id, highlightedClaimNumber]);

  return (
    <main
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px",
        background: "#fff",
      }}
    >
      <div style={{ marginBottom: "16px" }}>
        <h1
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "6px",
            color: partyTheme.chrome.heading,
            lineHeight: 1.25,
            borderBottom: `1px solid ${partyTheme.chrome.titleRule}`,
            paddingBottom: "8px",
          }}
        >
          Patent Reader
        </h1>
        {!loading && patent ? (
          <>
            <div
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: partyTheme.chrome.heading,
                lineHeight: 1.35,
                marginBottom: "4px",
              }}
            >
              {formatPatentDisplayCaption(
                trimmedPatentName,
                publicPatentId || recordPatentId,
              )}
            </div>
            {sourceLabel ? (
              <div
                style={{ fontSize: "11px", color: "#6b6b69", lineHeight: 1.35 }}
              >
                {sourceLabel}
              </div>
            ) : null}
          </>
        ) : (
          <div style={{ fontSize: "12px", color: "#5c5a54", lineHeight: 1.35 }}>
            {sourceLabel || "Select a processed patent ID from the sidebar"}
          </div>
        )}
      </div>

      {loading && (
        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
          Loading patent claims...
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            padding: "10px 12px",
            border: "1px solid #e9bcbc",
            background: "#fdecec",
            color: "#8f1e1e",
            fontSize: "12px",
            lineHeight: 1.35,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && !patent && (
        <div
          style={{
            border: `1px solid ${partyTheme.chrome.cardBorder}`,
            background: "#faf9f7",
            padding: "18px",
            fontSize: "12px",
            lineHeight: 1.4,
            color: "#6a6255",
          }}
        >
          Choose a processed patent ID from either portfolio to read full claims.
        </div>
      )}

      {!loading && !error && patent && (
        <article
          style={{
            border: `1px solid ${partyTheme.chrome.cardBorder}`,
            background: "#fff",
            padding: "14px 16px",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            {trimmedPatentName ? (
              <>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: partyTheme.chrome.heading,
                    lineHeight: 1.3,
                  }}
                >
                  {trimmedPatentName}
                </div>
                <div
                  style={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: partyTheme.chrome.heading,
                    lineHeight: 1.35,
                    marginTop: "6px",
                  }}
                >
                  {publicPatentId || recordPatentId || "—"}
                </div>
              </>
            ) : (
              <div
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: partyTheme.chrome.heading,
                  lineHeight: 1.25,
                }}
              >
                {publicPatentId || recordPatentId || "Unknown patent"}
              </div>
            )}
            {showRecordIdRow && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#7a756c",
                  marginTop: "4px",
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                Record ID: {recordPatentId}
              </div>
            )}
            <div style={{ fontSize: "11px", color: "#5c5a54", marginTop: "6px" }}>
              {claims.length} claim{claims.length === 1 ? "" : "s"}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {claims.map((claim) => (
              <section
                key={claim.claim_id || claim.claim_number}
                ref={(node) => {
                  const key =
                    claim?.claim_number != null ? String(claim.claim_number) : null;
                  if (!key) return;
                  if (node) claimRefs.current[key] = node;
                  else delete claimRefs.current[key];
                }}
                style={{
                  marginLeft:
                    String(claim.claim_number) === String(activeClaimNumber)
                      ? "-8px"
                      : 0,
                  marginRight:
                    String(claim.claim_number) === String(activeClaimNumber)
                      ? "-8px"
                      : 0,
                  paddingLeft:
                    String(claim.claim_number) === String(activeClaimNumber)
                      ? "8px"
                      : 0,
                  paddingRight:
                    String(claim.claim_number) === String(activeClaimNumber)
                      ? "8px"
                      : 0,
                  borderTop: "1px solid #ece8e1",
                  paddingTop: "10px",
                  background:
                    String(claim.claim_number) === String(activeClaimNumber)
                      ? "#fff8e8"
                      : "transparent",
                  boxShadow:
                    String(claim.claim_number) === String(activeClaimNumber)
                      ? "inset 0 0 0 1px #e3c488"
                      : "none",
                }}
              >
                <button
                  type="button"
                  onClick={() => jumpToClaim(claim.claim_number)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    fontSize: "10px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#6b4e2d",
                    marginBottom: "4px",
                    fontWeight: 600,
                    textAlign: "left",
                  }}
                >
                  Claim {claim.claim_number}
                </button>
                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: 1.5,
                    color: "#2f2f2d",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {parseClaimReferences(claim.claim || "").map((part, index) =>
                    part.type === "text" ? (
                      <span key={index}>{part.content}</span>
                    ) : (
                      <button
                        key={index}
                        type="button"
                        onClick={() => jumpToClaim(part.claimNumber)}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          color: partyTheme.theirs.textLabel,
                          fontWeight: 600,
                          textDecoration: "underline",
                          textDecorationStyle: "dotted",
                          cursor: availableClaimNumbers.has(
                            String(part.claimNumber),
                          )
                            ? "pointer"
                            : "default",
                        }}
                        disabled={
                          !availableClaimNumbers.has(String(part.claimNumber))
                        }
                      >
                        {part.content}
                      </button>
                    ),
                  )}
                </div>
              </section>
            ))}
          </div>
        </article>
      )}
    </main>
  );
}

export default AccountPatentReader;
